import { Grid } from "@mui/material";
import dayjs from "dayjs";
import { useGetStockPerformance } from "../services/AnalyticsPageService";
import storage from "services/storage";
import { formatNumber } from "utils/formatNumber";
import InputDateRangePickerComponent from "components/input-date-range-picker/InputDateRangePickerComponent";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";
import { useCallback, useState } from "react";
import { formatDate } from "utils/formatDate";

const StockPerformance = ({ closePriceResp }) => {
  const companyIdLocalStorage = storage.getCompanyID();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = paramParsify(location?.search);
  const [firstChange, setFirstChange] = useState(true);

  const { response: marketCapResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "MARKET_CAP_PRICE",
  });
  const { response: totalSharesResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "COMPANY_TOTAL_SHARES",
  });
  const { response: lowPriceResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "LOW_PRICE",
  });
  const { response: highPriceResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "HIGH_PRICE",
  });
  const { response: avgPriceResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "AVG_PRICE",
  });
  const { response: lowVolumeResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "LOWEST_VOLM",
  });
  const { response: highVolumeResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "HIGHEST_VOLM",
  });
  const { response: avgVolumeResp } = useGetStockPerformance({
    id: companyIdLocalStorage,
    type: "AVG_VOLM",
  });

  const handleOnSelectDate = useCallback(
    (payload) => {
      const stringifiedParams = paramStringify({
        ...(firstChange && !queryParams?.date
          ? { date: formatDate.toAPIDate(closePriceResp?.data?.end_date) }
          : {}),
        ...(firstChange && !queryParams?.period_start
          ? {
              period_end: formatDate.toAPIDate(closePriceResp?.data?.end_date),
              period_start: formatDate.toAPIDate(
                closePriceResp?.data?.start_date,
              ),
            }
          : {}),
        ...queryParams,
        page: 1,
        start_date: formatDate.toAPIDate(payload?.value?.dateStart),
        end_date: formatDate.toAPIDate(payload?.value?.dateEnd),
      });
      setFirstChange(false);
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
    },
    [location?.pathname, navigate, queryParams, closePriceResp, firstChange],
  );

  return (
    <div className='section'>
      <div className='flex flex-row justify-between items-center mb-5'>
        <div className='flex flex-row gap-2 items-center'>
          <p className='font-semibold'>Stock Performance</p>
        </div>
        <div className='flex gap-2 items-center'>
          <InputDateRangePickerComponent
            valueDateStart={dayjs(
              queryParams?.start_date || closePriceResp?.data?.start_date,
            )}
            valueDateEnd={dayjs(
              queryParams?.end_date || closePriceResp?.data?.end_date,
            )}
            onSelect={handleOnSelectDate}
            disableWeekends={true}
            minDate={
              dayjs().subtract(5, 'year').startOf('year').isAfter(dayjs('2021-01-01'))
                ? dayjs().subtract(5, 'year').startOf('year')
                : dayjs('2021-01-01')
            }
          />
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/closing-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Closing Price (IDR)</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(closePriceResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/script-shares-section-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Total Shares</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(totalSharesResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/market-cap-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Market Cap (IDR)</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(marketCapResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>

        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/highest-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Highest Stock Price (IDR)</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(highPriceResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/lowest-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Lowest Stock Price (IDR)</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(lowPriceResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/average-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Average Stock Price (IDR)</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(avgPriceResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/highest-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Highest Volume</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(highVolumeResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/lowest-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Lowest Volume</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(lowVolumeResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div
            className='section'
            style={{
              backgroundImage: "url(/images/average-stock-price-icon.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
            }}
          >
            <div className='flex flex-col gap-2'>
              <p className='text-[#5F737F]'>Average Stock Volume</p>
              <p className='font-bold text-2xl'>
                {formatNumber.toAmount(avgVolumeResp?.data?.value ?? 0)}
              </p>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default StockPerformance;
